import type { GpsCapture, SalesDraft, SubmitPayload } from './types';
import { ACTIVITY_SUPPORT_STORE_ID } from './activity-support';


// Legacy drafts did not persist the first-send payload. Their GPS capture is the
// nearest stable evidence of the original browser date; updated_at is autosaved.
function capturedBrowserDate(gps: GpsCapture, fallback: string): string {
  const date = new Date(gps.captured_at);
  if (!Number.isFinite(date.getTime())) return fallback;
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export function buildDraftSubmissionPayload(current: SalesDraft, gps: GpsCapture, submissionDate: string): SubmitPayload {
  if (current.phase !== 'EDITING' && current.submission_payload) {
    return structuredClone(current.submission_payload);
  }
  const originalDate = current.phase !== 'EDITING' ? capturedBrowserDate(gps, submissionDate) : submissionDate;
  const isBackfill = current.is_backfill || current.business_date !== originalDate;
  const selectOther = (value: string, other: string) => value === '其他' ? `其他：${other.trim()}` : value;
  return {
    visit_id: current.visit_id,
    schema_version: 'sales-visit-v1',
    business_date: current.business_date,
    contact_at: new Date(`${current.business_date}T${current.contact_time}:00+08:00`).toISOString(),
    reporter_id: current.reporter_id,
    temporary_reporter_name: current.reporter_id === 'temporary' ? current.temporary_reporter_name.trim() : '',
    store_id: current.store_id,
    ...(current.store_id === ACTIVITY_SUPPORT_STORE_ID ? { activity_id: current.activity_id || '' } : {}),
    result: current.result,
    answers: {
      gender: current.gender,
      age_group: current.age_group,
      identity: selectOther(current.identity, current.identity_other),
      size: current.size,
      motivation: [selectOther(current.motivation, current.motivation_other)],
      purpose: [selectOther(current.purpose, current.purpose_other)],
      no_sale_reasons: current.result === 'NOT_SOLD' ? [...current.no_sale_reasons] : [],
      no_sale_reason_other: current.result === 'NOT_SOLD' && current.no_sale_reasons.includes('其他')
        ? current.no_sale_reason_other.trim() : '',
      competitor_brand: current.result === 'NOT_SOLD' ? selectOther(current.competitor_brand, current.competitor_brand_other) : '',
      competitor_model: current.result === 'NOT_SOLD' ? current.competitor_model.trim() : '',
      summary: current.summary.trim()
    },
    items: current.result === 'SOLD'
      ? current.items.map(item => ({ item_id: item.item_id, model_id: item.model_id, quantity: item.quantity })) : [],
    gps: { ...gps },
    source: 'WEB',
    is_backfill: isBackfill,
    backfill_reason: isBackfill ? current.backfill_reason.trim() : '',
    test_mode: current.reporter_id === 'test'
  };
}

const DB_NAME = 'mnt-sales-report';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
const ACTIVE_KEY = 'active';
const DRAFT_KEY_PREFIX = 'draft:';
const LOCK_KEY_PREFIX = 'lock:';
const LEGACY_FALLBACK_KEY = 'mnt-sales-report:draft:v1';
const FALLBACK_KEY_PREFIX = 'mnt-sales-report:draft:v1:';
const REPORTER_KEY = 'mnt-sales-report:reporter:v1';
const STORE_KEY = 'mnt-sales-report:store:v1';
const FALLBACK_LOCK_PREFIX = 'mnt-sales-report:lock:v1:';

export type DraftNamespace = 'formal' | 'test';

function indexedDraftPrefix(namespace: DraftNamespace): string {
  return `${DRAFT_KEY_PREFIX}${namespace}:`;
}

function fallbackDraftPrefix(namespace: DraftNamespace): string {
  return `${FALLBACK_KEY_PREFIX}${namespace}:`;
}

function isLegacyIndexedDraftKey(key: string): boolean {
  return key.startsWith(DRAFT_KEY_PREFIX)
    && !key.startsWith(indexedDraftPrefix('formal'))
    && !key.startsWith(indexedDraftPrefix('test'));
}

function indexedKeyMatchesNamespace(key: string, namespace: DraftNamespace): boolean {
  return key.startsWith(indexedDraftPrefix(namespace))
    || (namespace === 'formal' && (key === ACTIVE_KEY || isLegacyIndexedDraftKey(key)));
}

function fallbackKeyMatchesNamespace(key: string, namespace: DraftNamespace): boolean {
  if (key.startsWith(fallbackDraftPrefix(namespace))) return true;
  if (namespace !== 'formal') return false;
  if (key === LEGACY_FALLBACK_KEY) return true;
  if (!key.startsWith(FALLBACK_KEY_PREFIX)) return false;
  const suffix = key.slice(FALLBACK_KEY_PREFIX.length);
  return !suffix.startsWith('formal:') && !suffix.startsWith('test:');
}

export interface DraftOwnership {
  renew(): Promise<void>;
  release(): Promise<void>;
}

interface StoredDraftLock {
  owner_id: string;
  expires_at: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB unavailable'));
  });
}

function lockLooksActive(value: unknown, now = Date.now()): value is StoredDraftLock {
  return Boolean(value && typeof value === 'object'
    && typeof (value as StoredDraftLock).owner_id === 'string'
    && Number((value as StoredDraftLock).expires_at) > now);
}

async function claimIndexedDbLock(visitId: string, ownerId: string): Promise<boolean> {
  const database = await openDatabase();
  try {
    return await new Promise<boolean>((resolve, reject) => {
      let claimed = false;
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const key = `${LOCK_KEY_PREFIX}${visitId}`;
      const request = store.get(key);
      request.onsuccess = () => {
        const current = request.result as unknown;
        if (!lockLooksActive(current) || current.owner_id === ownerId) {
          store.put({ owner_id: ownerId, expires_at: Date.now() + 15_000 } satisfies StoredDraftLock, key);
          claimed = true;
        }
      };
      transaction.oncomplete = () => resolve(claimed);
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB lock failed'));
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB lock aborted'));
    });
  } finally {
    database.close();
  }
}

async function updateIndexedDbLock(visitId: string, ownerId: string, release: boolean): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const key = `${LOCK_KEY_PREFIX}${visitId}`;
      const request = store.get(key);
      request.onsuccess = () => {
        const current = request.result as StoredDraftLock | undefined;
        if (current?.owner_id !== ownerId) return;
        if (release) store.delete(key);
        else store.put({ owner_id: ownerId, expires_at: Date.now() + 15_000 } satisfies StoredDraftLock, key);
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB lock failed'));
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB lock aborted'));
    });
  } finally {
    database.close();
  }
}

function claimFallbackLock(visitId: string, ownerId: string): boolean {
  const key = `${FALLBACK_LOCK_PREFIX}${visitId}`;
  try {
    const current = JSON.parse(localStorage.getItem(key) || 'null') as StoredDraftLock | null;
    if (lockLooksActive(current) && current.owner_id !== ownerId) return false;
    localStorage.setItem(key, JSON.stringify({ owner_id: ownerId, expires_at: Date.now() + 15_000 }));
    const written = JSON.parse(localStorage.getItem(key) || 'null') as StoredDraftLock | null;
    return written?.owner_id === ownerId;
  } catch {
    return false;
  }
}

export async function claimDraftOwnership(visitId: string): Promise<DraftOwnership | null> {
  const lockName = `mnt-sales-draft:${visitId}`;
  if (navigator.locks?.request) {
    let releaseHold: (() => void) | null = null;
    let resolveClaim!: (ownership: DraftOwnership | null) => void;
    const claim = new Promise<DraftOwnership | null>(resolve => { resolveClaim = resolve; });
    const hold = new Promise<void>(resolve => { releaseHold = resolve; });
    void navigator.locks.request(lockName, { mode: 'exclusive', ifAvailable: true }, async lock => {
      if (!lock) {
        resolveClaim(null);
        return;
      }
      let released = false;
      resolveClaim({
        renew: async () => undefined,
        release: async () => {
          if (released) return;
          released = true;
          releaseHold?.();
        }
      });
      await hold;
    }).catch(() => resolveClaim(null));
    return claim;
  }

  const ownerId = crypto.randomUUID();
  let indexedDb = false;
  try {
    indexedDb = await claimIndexedDbLock(visitId, ownerId);
  } catch {
    indexedDb = false;
  }
  if (!indexedDb && !claimFallbackLock(visitId, ownerId)) return null;
  let released = false;
  return {
    renew: async () => {
      if (released) return;
      if (indexedDb) await updateIndexedDbLock(visitId, ownerId, false).catch(() => undefined);
      else claimFallbackLock(visitId, ownerId);
    },
    release: async () => {
      if (released) return;
      released = true;
      if (indexedDb) await updateIndexedDbLock(visitId, ownerId, true).catch(() => undefined);
      try {
        const key = `${FALLBACK_LOCK_PREFIX}${visitId}`;
        const current = JSON.parse(localStorage.getItem(key) || 'null') as StoredDraftLock | null;
        if (current?.owner_id === ownerId) localStorage.removeItem(key);
      } catch {
        // Browser teardown can make storage unavailable; the lease expires.
      }
    }
  };
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
    });
  } finally {
    database.close();
  }
}

function isDraft(value: unknown): value is SalesDraft {
  return Boolean(value && typeof value === 'object' && (value as SalesDraft).storage_version === 1
    && typeof (value as SalesDraft).visit_id === 'string');
}

function newestDraft(values: unknown[]): SalesDraft | null {
  const byVisit = new Map<string, SalesDraft>();
  for (const value of values) {
    if (!isDraft(value)) continue;
    const previous = byVisit.get(value.visit_id);
    if (!previous || value.updated_at > previous.updated_at) byVisit.set(value.visit_id, value);
  }
  return Array.from(byVisit.values()).sort((left, right) => right.updated_at.localeCompare(left.updated_at))[0] || null;
}

function readFallbackDraft(namespace: DraftNamespace): SalesDraft | null {
  try {
    const candidates: unknown[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || !fallbackKeyMatchesNamespace(key, namespace)) continue;
      const raw = localStorage.getItem(key);
      if (raw) candidates.push(JSON.parse(raw));
    }
    return newestDraft(candidates);
  } catch {
    return null;
  }
}

async function readIndexedDrafts(namespace: DraftNamespace): Promise<SalesDraft[]> {
  const database = await openDatabase();
  try {
    return await new Promise<SalesDraft[]>((resolve, reject) => {
      const drafts: SalesDraft[] = [];
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const request = transaction.objectStore(STORE_NAME).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        if (indexedKeyMatchesNamespace(String(cursor.key), namespace) && isDraft(cursor.value)) {
          drafts.push(cursor.value);
        }
        cursor.continue();
      };
      transaction.oncomplete = () => resolve(drafts);
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
    });
  } finally {
    database.close();
  }
}

export async function loadDraft(namespace: DraftNamespace = 'formal'): Promise<SalesDraft | null> {
  try {
    const values = await readIndexedDrafts(namespace);
    return newestDraft(values) || readFallbackDraft(namespace);
  } catch {
    return readFallbackDraft(namespace);
  }
}

export async function clearAllDrafts(namespace: DraftNamespace = 'formal'): Promise<void> {
  let indexedDbAvailable = false;
  let indexedDbCleared = false;
  try {
    const database = await openDatabase();
    indexedDbAvailable = true;
    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          const key = String(cursor.key);
          if (indexedKeyMatchesNamespace(key, namespace)) cursor.delete();
          cursor.continue();
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
        transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
      });
      indexedDbCleared = true;
    } finally {
      database.close();
    }
  } catch {
    // Browsers without IndexedDB can still use the localStorage fallback.
  }
  let fallbackCleared = false;
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && fallbackKeyMatchesNamespace(key, namespace)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
    fallbackCleared = true;
  } catch {
    // IndexedDB is the primary store when available.
  }
  if ((indexedDbAvailable && !indexedDbCleared) || (!indexedDbCleared && !fallbackCleared)) {
    throw new Error('無法完整清除原草稿，請重新整理後再試。');
  }
}

export async function saveDraft(draft: SalesDraft, namespace: DraftNamespace = 'formal'): Promise<void> {
  try {
    // Keep the event ID in the key so completion can clear only the exact
    // receipt. The explicit new-report action removes every older draft first.
    await withStore<IDBValidKey>('readwrite', store => (
      store.put(draft, `${indexedDraftPrefix(namespace)}${draft.visit_id}`)
    ));
    try {
      localStorage.removeItem(`${fallbackDraftPrefix(namespace)}${draft.visit_id}`);
      if (namespace === 'formal') {
        localStorage.removeItem(LEGACY_FALLBACK_KEY);
        localStorage.removeItem(`${FALLBACK_KEY_PREFIX}${draft.visit_id}`);
      }
    } catch {
      // IndexedDB already has the durable copy.
    }
  } catch {
    try {
      localStorage.setItem(`${fallbackDraftPrefix(namespace)}${draft.visit_id}`, JSON.stringify(draft));
    } catch {
      throw new Error('這個瀏覽器目前無法保存草稿');
    }
  }
}

export async function clearDraft(expectedVisitId: string, namespace: DraftNamespace = 'formal'): Promise<void> {
  try {
    const database = await openDatabase();
    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        store.delete(`${indexedDraftPrefix(namespace)}${expectedVisitId}`);
        if (namespace === 'formal') {
          store.delete(`${DRAFT_KEY_PREFIX}${expectedVisitId}`);
          const legacyRequest = store.get(ACTIVE_KEY);
          legacyRequest.onsuccess = () => {
            if (isDraft(legacyRequest.result) && legacyRequest.result.visit_id === expectedVisitId) {
              store.delete(ACTIVE_KEY);
            }
          };
        }
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
        transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
      });
    } finally {
      database.close();
    }
  } catch {
    // Continue to clear the fallback copy.
  }
  try {
    localStorage.removeItem(`${fallbackDraftPrefix(namespace)}${expectedVisitId}`);
    if (namespace === 'formal') {
      localStorage.removeItem(`${FALLBACK_KEY_PREFIX}${expectedVisitId}`);
      const legacy = localStorage.getItem(LEGACY_FALLBACK_KEY);
      if (legacy) {
        const parsed = JSON.parse(legacy) as unknown;
        if (isDraft(parsed) && parsed.visit_id === expectedVisitId) localStorage.removeItem(LEGACY_FALLBACK_KEY);
      }
    }
  } catch {
    // A completed server write must not be reversed by localStorage availability.
  }
}

export function loadRememberedReporter(): string {
  try {
    return localStorage.getItem(REPORTER_KEY) || '';
  } catch {
    return '';
  }
}

export function rememberReporter(reporterId: string): void {
  try {
    if (reporterId) localStorage.setItem(REPORTER_KEY, reporterId);
    else localStorage.removeItem(REPORTER_KEY);
  } catch {
    // Remembering a convenience field must never block reporting.
  }
}

export function loadRememberedStore(): string {
  try {
    return localStorage.getItem(STORE_KEY) || '';
  } catch {
    return '';
  }
}

export function rememberStore(storeId: string): void {
  try {
    if (storeId) localStorage.setItem(STORE_KEY, storeId);
    else localStorage.removeItem(STORE_KEY);
  } catch {
    // Remembering a convenience field must never block reporting.
  }
}
